"use client";

import {
  CalendarDaysIcon,
  Code2Icon,
  CreditCardIcon,
  FlaskConicalIcon,
  GaugeIcon,
  HourglassIcon,
  MailIcon,
  NewspaperIcon,
  Shield,
  SlidersVerticalIcon,
  SparklesIcon,
  TheaterIcon,
  TicketIcon,
  UserCircleIcon,
  UsersIcon,
  VideoIcon,
  ZapIcon,
} from "lucide-react";
import { DashboardMenuItemWithPath, DashboardSidebarMenuItem } from "./types";
import AdsIcon from "./AdsIcon";

import { Comedian } from "@punchup/data";
import { useGate } from "statsig-react";
import { useMediaQuery } from "@punchup/utils";
import { createElement, useMemo } from "react";
import { usePathname } from "next/navigation";
import { BetaBadge } from "../common/BetaBadge";

const AUDIENCES_PATH = "/dashboard/audiences";
const NEWSLETTER_PATH = "/dashboard/messages/newsletter";
const AUTOMATIONS_PATH = "/dashboard/messages/automations";

// Menu items with expandable subitems (contact_import_web gate ON)
export const baseComedianMenuItemsWithSubitems: DashboardMenuItemWithPath[] = [
  {
    label: "Recommendations",
    title: "Recommendations",
    path: "/dashboard/recommendations",
    Icon: SparklesIcon,
    active: false,
  },
  {
    label: "Audiences",
    title: "Audiences",
    path: AUDIENCES_PATH,
    Icon: TheaterIcon,
    active: false,
    subitems: [
      {
        label: "Punchup Guest List",
        title: "Punchup guest list",
        path: "/dashboard/audience",
        Icon: TicketIcon,
        active: false,
      },
      {
        label: "Uploaded Contacts",
        title: "Uploaded contacts",
        path: "/dashboard/audiences/imported",
        Icon: UsersIcon,
        active: false,
      },
    ],
  },
  {
    label: "Deep Dive",
    title: "Deep dive",
    path: "/dashboard/deep-dive",
    Icon: SlidersVerticalIcon,
    active: false,
  },
  {
    label: "Tour Dates",
    title: "Tour dates",
    path: "/dashboard/tour-dates",
    Icon: CalendarDaysIcon,
    active: false,
    subitems: [
      {
        label: "Manage",
        title: "Manage",
        path: "/dashboard/tour-dates",
        Icon: CalendarDaysIcon,
        active: false,
      },
      {
        label: "Waitlist",
        title: "Waitlist",
        path: "/dashboard/waitlist",
        Icon: HourglassIcon,
        active: false,
      },
    ],
  },
  {
    label: "Messages",
    title: "Messages",
    path: "/dashboard/messages",
    Icon: MailIcon,
    active: false,
    // Newsletter and Automations are each gated in the hook below
    // (`newsletter_tab`, `automations_tab`); when only Email and SMS
    // survives, Messages flattens back to today's plain item.
    subitems: [
      {
        label: "Email and SMS",
        title: "Email & SMS",
        path: "/dashboard/messages",
        Icon: MailIcon,
        active: false,
      },
      {
        label: "Newsletter",
        title: "Newsletter",
        path: NEWSLETTER_PATH,
        Icon: NewspaperIcon,
        active: false,
        badge: createElement(BetaBadge),
      },
      {
        label: "Automations",
        title: "Automations",
        path: AUTOMATIONS_PATH,
        Icon: ZapIcon,
        active: false,
        // The in-tab checkout keeps Automations highlighted.
        matchChildPaths: true,
      },
    ],
  },
  {
    label: "Manage Content",
    title: "Manage content",
    path: "/dashboard/content",
    Icon: VideoIcon,
    active: false,
  },
  {
    label: "Ads",
    title: "Ads",
    path: "/dashboard/ads",
    Icon: AdsIcon,
    active: false,
  },
];

// Menu items without subitems (contact_import_web gate OFF)
export const baseComedianMenuItemsWithoutSubitems: DashboardMenuItemWithPath[] =
  [
    {
      label: "Recommendations",
      title: "Recommendations",
      path: "/dashboard/recommendations",
      Icon: SparklesIcon,
      active: false,
    },
    {
      label: "Audience",
      title: "Audience",
      path: "/dashboard/audience",
      Icon: TheaterIcon,
      active: false,
    },
    {
      label: "Deep Dive",
      title: "Deep dive",
      path: "/dashboard/deep-dive",
      Icon: SlidersVerticalIcon,
      active: false,
    },
    {
      label: "Tour Dates",
      title: "Tour dates",
      path: "/dashboard/tour-dates",
      Icon: CalendarDaysIcon,
      active: false,
    },
    {
      label: "Waitlist",
      title: "Waitlist",
      path: "/dashboard/waitlist",
      Icon: HourglassIcon,
      active: false,
    },
    {
      label: "Messages",
      title: "Messages",
      path: "/dashboard/messages",
      Icon: MailIcon,
      active: false,
    },
    {
      label: "Manage Content",
      title: "Manage content",
      path: "/dashboard/content",
      Icon: VideoIcon,
      active: false,
    },
    {
      label: "Ads",
      title: "Ads",
      path: "/dashboard/ads",
      Icon: AdsIcon,
      active: false,
    },
  ];

// The comedian's settings pages: the Profile group's rows, and the settings nav.
export const comedianSettingsMenuItems: DashboardMenuItemWithPath[] = [
  {
    label: "Public Profile",
    title: "Public profile",
    path: "/dashboard/profile",
    Icon: UserCircleIcon,
    active: false,
  },
  {
    label: "Managers & Agents",
    title: "Managers & agents",
    path: "/dashboard/contacts",
    Icon: UsersIcon,
    active: false,
  },
];

// Plan & usage and Billing, appended to the settings nav when the Automations gate is on.
// Kept out of `comedianSettingsMenuItems`: the legacy Profile group renders that array's
// rows with the settings gate off.
export const comedianPlanBillingMenuItems: DashboardMenuItemWithPath[] = [
  {
    label: "Plan & Usage",
    title: "Plan & usage",
    path: "/dashboard/plan-usage",
    Icon: GaugeIcon,
    active: false,
  },
  {
    label: "Billing",
    title: "Billing",
    path: "/dashboard/billing",
    Icon: CreditCardIcon,
    active: false,
  },
];

// The rows after the gated Moderation row, so they cannot join the arrays above.
// The Profile group renders as one collapsible section in every nav.
export const baseComedianTrailingMenuItems: DashboardMenuItemWithPath[] = [
  {
    label: "Profile",
    title: "Profile",
    path: "/dashboard/profile",
    Icon: UserCircleIcon,
    active: false,
    subitems: comedianSettingsMenuItems,
  },
  {
    label: "Labs",
    title: "Labs",
    path: "/dashboard/labs",
    Icon: FlaskConicalIcon,
    active: false,
  },
  {
    label: "Developers",
    title: "Developers",
    path: "/dashboard/developers",
    Icon: Code2Icon,
    active: false,
  },
];

interface UseDashboardMenuItemsOptions {
  comedianSlug: string;
  selectedComedian: Comedian | null;
  showAllSubitems?: boolean;
  /** The settings gate's answer. Its nav carries the Profile group's pages. */
  settingsNav?: boolean;
}

export function useDashboardMenuItems({
  comedianSlug,
  selectedComedian,
  showAllSubitems = false,
  settingsNav = false,
}: UseDashboardMenuItemsOptions): DashboardSidebarMenuItem[] {
  const pathname = usePathname();
  const { value: videoCommentsEnabled } = useGate("video_comments_stableid");
  const { value: newsletterTabEnabled } = useGate("newsletter_tab");
  const { value: automationsTabEnabled } = useGate("automations_tab");
  const { value: contactImportEnabled } = useGate("contact_import_web");
  const { value: improvedAudienceListEnabled } = useGate(
    "artists-improved-audience-list"
  );
  const { value: useCaseMessagingEnabled } = useGate("use_case_messaging");
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  const comedianMenuItems = useMemo(() => {
    let items = contactImportEnabled
      ? [...baseComedianMenuItemsWithSubitems]
      : [...baseComedianMenuItemsWithoutSubitems];

    // Gate on: "All Contacts" replaces the legacy "Punchup Guest List" subitem,
    // "Uploaded Contacts" becomes "Lists", and the group reads "Audience" under
    // the Users icon with a "New" badge in both sidebars.
    if (improvedAudienceListEnabled) {
      items = items.map((item) => {
        if (item.path !== AUDIENCES_PATH) {
          return item;
        }
        return {
          ...item,
          label: "Audience",
          title: "Audience",
          Icon: UsersIcon,
          badge: createElement(BetaBadge, { label: "New" }),
          badgeLabel: "New",
          subitems: item.subitems?.map((subitem) => {
            if (subitem.label === "Punchup Guest List") {
              return {
                ...subitem,
                label: "All Contacts",
                title: "All contacts",
                path: "/dashboard/audiences/all-contacts/list",
                activePaths: [
                  "/dashboard/audiences/all-contacts/list",
                  "/dashboard/audiences/all-contacts/map",
                ],
              };
            }
            if (subitem.path === "/dashboard/audiences/imported") {
              return {
                ...subitem,
                label: "Lists",
                title: "Lists",
                path: "/dashboard/audiences/lists",
                matchChildPaths: true,
              };
            }
            return subitem;
          }),
        };
      });
    }

    // On mobile, hide the "Uploaded Contacts" subitem for gate-passing users
    // unless showAllSubitems is true (used by the mobile drawer/accordion)
    if (contactImportEnabled && !isDesktop && !showAllSubitems) {
      items = items.map((item) => {
        if (item.path === AUDIENCES_PATH) {
          return {
            ...item,
            label: "Audience",
            title: "Audience",
            path: item.subitems?.[0]?.path ?? item.path,
            subitems: undefined,
          };
        }
        return item;
      });
    }

    if (videoCommentsEnabled) {
      items.push({
        label: "Moderation",
        title: "Comment moderation",
        path: "/dashboard/moderation",
        Icon: Shield,
        active: false,
      });
    }

    items.push(
      ...baseComedianTrailingMenuItems.filter(
        (item) => !settingsNav || item.label !== "Profile"
      )
    );

    if (useCaseMessagingEnabled) {
      items = items.map((item) => {
        if (item.label === "Messages") {
          return {
            ...item,
            // Two renderings of ONE badge: `badge` for the legacy sidebar / mobile
            // drawer, `badgeLabel` for the green-tier PunchupDashboardSidebar, which
            // draws its own BetaBadge. Keep them in step.
            badge: createElement(BetaBadge),
            badgeLabel: "Beta",
          };
        }
        return item;
      });
    }

    // Per-gate sub-item pruning under Messages: Newsletter rides
    // `newsletter_tab` (D14), Automations rides `automations_tab` (default
    // OFF). When only "Email and SMS" survives, flatten Messages back to
    // today's plain item — a single self-referential sub-item is just noise.
    items = items.map((item) => {
      if (item.label === "Messages" && item.subitems) {
        const subitems = item.subitems.filter((subitem) => {
          if (subitem.path === NEWSLETTER_PATH) return newsletterTabEnabled;
          if (subitem.path === AUTOMATIONS_PATH) return automationsTabEnabled;
          return true;
        });
        return subitems.length > 1
          ? { ...item, subitems }
          : { ...item, subitems: undefined };
      }
      return item;
    });

    return items;
  }, [
    automationsTabEnabled,
    contactImportEnabled,
    improvedAudienceListEnabled,
    isDesktop,
    newsletterTabEnabled,
    settingsNav,
    showAllSubitems,
    useCaseMessagingEnabled,
    videoCommentsEnabled,
  ]);

  const slug = selectedComedian?.slug ?? comedianSlug;

  const menuItems = useMemo(() => {
    const isCurrent = (entry: DashboardMenuItemWithPath, url: string) =>
      (entry.activePaths
        ? entry.activePaths.some((p) => pathname === `/${slug}${p}`)
        : pathname === url) ||
      (entry.matchChildPaths === true && pathname.startsWith(`${url}/`));

    return comedianMenuItems.map((item) => {
      const itemUrl = `/${slug}${item.path}`;
      const mappedSubitems = item.subitems?.map((subitem) => {
        const subitemUrl = `/${slug}${subitem.path}`;
        const { path: _path, subitems: _subitems, ...restSubitem } = subitem;
        return {
          ...restSubitem,
          active: isCurrent(subitem, subitemUrl),
          url: subitemUrl,
        };
      });

      const hasActiveSubitem = mappedSubitems?.some((sub) => sub.active);

      const isActive = isCurrent(item, itemUrl);

      const { path: _itemPath, subitems: _itemSubitems, ...restItem } = item;
      return {
        ...restItem,
        active: !hasActiveSubitem && isActive,
        url: itemUrl,
        subitems: mappedSubitems,
      };
    });
  }, [comedianMenuItems, slug, pathname]);

  return menuItems;
}
